import {type ReactNode} from 'react'
import {Link} from 'react-router-dom'

type AuthLayoutProps = {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly variant?: 'embedded' | 'standalone';
}

export function AuthLayout({title, subtitle, children, footer, variant = 'embedded'}: AuthLayoutProps) {
  // `embedded` : carte centrée dans l'espace restant sous le header public.
  // `standalone` : plein écran autonome (page /account, hors layout public).
  const wrapperClass = variant === 'standalone'
    ? 'flex min-h-screen w-full items-center justify-center bg-background px-4 py-12'
    : 'flex w-full flex-1 items-center justify-center px-4 py-12'

  return (
    <div className={wrapperClass}>
      <div className='flex w-full max-w-sm flex-col gap-6'>
        <div className='flex flex-col gap-2 text-center'>
          <h1 className='text-xl font-semibold tracking-tight'>{title}</h1>
          {subtitle && <p className='text-sm text-muted-foreground'>{subtitle}</p>}
        </div>
        {children}
        {footer && <div className='text-center text-sm text-muted-foreground'>{footer}</div>}
      </div>
    </div>
  )
}

export const fieldClass
  = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none focus:ring-2 focus:ring-ring'

export const primaryButtonClass = [
  'inline-flex w-full cursor-pointer items-center justify-center rounded-md',
  'bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow',
  'transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60'
].join(' ')

export const secondaryButtonClass = [
  'inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-md',
  'border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm',
  'transition-colors hover:bg-accent hover:text-accent-foreground',
  'disabled:cursor-not-allowed disabled:opacity-60'
].join(' ')

export function ErrorBanner({message}: {readonly message: string}) {
  return (
    <div className='rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive'>
      {message}
    </div>
  )
}

export function SuccessBanner({message}: {readonly message: string}) {
  return (
    <div className='rounded-md border border-green-600/40 bg-green-600/10 px-4 py-3 text-sm text-green-700 dark:text-green-400'>
      {message}
    </div>
  )
}

export function AuthLink({to, children}: {readonly to: string; readonly children: ReactNode}) {
  return (
    <Link to={to} className='font-medium text-primary hover:underline'>
      {children}
    </Link>
  )
}
